<template>
  <v-switch ref="switch" class="brand-switch" v-bind="mergedAttrs">
    <template v-for="(_, slotName) in $slots" :key="slotName" #[slotName]="slotData">
      <slot :name="slotName" v-bind="slotData || {}" />
    </template>
  </v-switch>
</template>

<script setup>
import { computed, useAttrs, ref } from 'vue'
import { mergeAttrs } from '@/utils/vue-attrs'

const props = defineProps({
  variant: { type: String, default: null },
})

const attrs = useAttrs()
const switchRef = ref(null)

const VARIANTS = {
  basic: { inset: true, ripple: false, classes: ['brand-switch--basic'] },
}

const commonAttrs = computed(() => {
  const variant = VARIANTS[props.variant]
  const classes = []
  const common = { 'hide-details': 'auto' }
  if (!attrs.label) classes.push('no-label')
  return { ...common, ...variant, class: [...classes, ...(variant?.classes ?? [])] }
})

const mergedAttrs = computed(() => mergeAttrs(commonAttrs.value, attrs))

defineOptions({ inheritAttrs: false })
</script>

<script>
export default { inheritAttrs: false }
</script>

<style lang="scss">
/* Limit to BrandSwitch root class to avoid affecting other switches */
.brand-switch.no-label .v-input__control { margin: 0; }

/* OFF (light theme) */
.brand-switch.brand-switch--basic:not(.v-input--disabled) .v-switch__track {
  opacity: 1 !important;
  background-color: rgb(var(--v-theme-neutral50)) !important;
  box-shadow: none !important;
  border: none !important;
  border-radius: 999px;
}

/* OFF (dark theme) */
.v-theme--dark .brand-switch.brand-switch--basic:not(.v-input--disabled) .v-switch__track {
  background-color: rgb(var(--v-theme-neutral30)) !important;
  box-shadow: none !important;
  border: none !important;
}

/* ON (both themes) */
.brand-switch.brand-switch--basic .v-selection-control--dirty .v-switch__track {
  background-color: rgb(var(--v-theme-teal600)) !important;
  box-shadow: none !important;
  border: none !important;
}

/* ON (dark theme) — ensure this wins over dark OFF rule */
.v-theme--dark .brand-switch.brand-switch--basic:not(.v-input--disabled) .v-selection-control--dirty .v-switch__track {
  background-color: rgb(var(--v-theme-teal600)) !important;
  box-shadow: none !important;
  border: none !important;
}

/* Thumb (both themes) */
.brand-switch.brand-switch--basic .v-switch__thumb {
  background-color: rgb(var(--v-theme-neutral0)) !important;
  color: rgb(var(--v-theme-neutral0)) !important;
  border: 2px solid rgb(var(--v-theme-neutral0)) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
</style>


